/**
 * Sparkline - A minimal trend chart using SVG
 */

interface SparklineProps {
  data: number[];
  color?: string;
  height?: number;
  showArea?: boolean;
  label?: string;
}

export function Sparkline({
  data,
  color = 'var(--color-primary)', // blue-500
  height = 40,
  showArea = true,
  label,
}: SparklineProps) {
  if (!data || data.length < 2) {
    return (
      <div className="text-xs text-gray-400 py-4 text-center">
        Not enough data for chart
      </div>
    );
  }

  const width = 200;
  const padding = 2;

  // Normalize data to fit in the chart area
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  // Calculate points
  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * (width - padding * 2);
    const y = padding + (1 - (value - min) / range) * (height - padding * 2);
    return { x, y, value };
  });

  // Create SVG path
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Create area path (for fill)
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding} L ${padding} ${height - padding} Z`;

  // Calculate trend
  const firstHalf = data.slice(0, Math.floor(data.length / 2));
  const secondHalf = data.slice(Math.floor(data.length / 2));
  const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
  const trendUp = secondAvg > firstAvg;
  const trendPct = firstAvg !== 0
    ? Math.abs(((secondAvg - firstAvg) / firstAvg) * 100)
    : secondAvg > 0 ? 100 : 0;

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">{label}</span>
          <span className={`text-xs ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
            {trendUp ? '↑' : '↓'} {trendPct.toFixed(0)}%
          </span>
        </div>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ height }}
        preserveAspectRatio="none"
      >
        {/* Area fill */}
        {showArea && (
          <path
            d={areaPath}
            fill={color}
            fillOpacity={0.1}
          />
        )}

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* End point */}
        <circle
          cx={points[points.length - 1].x}
          cy={points[points.length - 1].y}
          r={3}
          fill={color}
        />
      </svg>

      {/* Min/Max labels */}
      <div className="flex justify-between text-[10px] text-gray-400 mt-1">
        <span>{data.length}d ago</span>
        <span>Today</span>
      </div>
    </div>
  );
}
